"use client";

import React from "react";
import Link from "next/link";
import VideoPlayerClient from "@/components/VideoPlayerClient";
import AudienceBadge from "@/components/content/AudienceBadge";
import { isPrivateAudience } from "@/lib/audience";
import { toAbsoluteMediaUrl } from "@/lib/media";
import { scrubContactInfo } from "@/lib/scrubContact";
import { apiFetch } from "@/lib/api";

type UploaderMini = { displayName?: string; profileUrl?: string; avatarUrl?: string } | null;

import { useSession } from 'next-auth/react';

export default function VideoDetailClient({
  id,
  streamUrl,
  viewUrl,
  posterUrl,
  unsafe,
  title,
  description,
  viewCount,
  gated,
  likeCount,
  isLiked,
  createdAt,
  uploader,
  initialDurationSec,
  tags,
  titleHeading,
  audience,
  recoveryShell = false,
}: {
  id: number | string;
  streamUrl: string;
  viewUrl: string;
  posterUrl?: string | null;
  unsafe?: boolean;
  title: string;
  description?: string | null;
  viewCount?: number | null;
  gated?: boolean;
  likeCount?: number;
  isLiked?: boolean;
  createdAt?: string | null;
  uploader?: UploaderMini;
  initialDurationSec?: number | null;
  tags?: string[] | null;
  // Passed straight to VideoPlayerClient. Left undefined (so it defaults to
  // 'h1') on the recovery shell where this is the page's only heading; the full
  // detail page passes 'h2' because it renders its own hero h1 with the same
  // text. See the note on VideoPlayerClient's titleHeading prop.
  titleHeading?: 'h1' | 'h2';
  // Who may be delivered this video, as the SSR JSON reported it
  // ('Public' | 'Friends' | 'Watchers'). Presentation only: a viewer only
  // reaches this page if the api already put them inside the audience.
  audience?: string | null;
  // True ONLY when rendered by page.tsx's ClientShell (placeholder metadata,
  // recovery via the client refetch). In that path a 404 from the refetch is
  // final, so we render a not-found state instead of a dead placeholder
  // player. The full detail page never sets this and is unaffected.
  recoveryShell?: boolean;
}) {
  const { data: session, status } = useSession();
  const guest = status === 'unauthenticated';
  const [meta, setMeta] = React.useState<{ durationSec?: number; width?: number; height?: number; sizeBytes?: number; bitrateKbps?: number }>({});
  const [headErr, setHeadErr] = React.useState<string | null>(null);
  const [resolvedUrl, setResolvedUrl] = React.useState<string | null>(null);
  const HEAD_PROBE = (process.env.NEXT_PUBLIC_VIDEO_HEAD_PROBE === '1');

  // Phase D §D.6 — SSR-without-auth narrow refetch. SSR fetches anonymously
  // (page.tsx getVideo(videoId, null)) to avoid the cookie-propagation race
  // documented in incident-2026-05-19-ssr-gating-removed. The guest render
  // can have a blurred thumbnail or scrubbed metadata that's wrong for the
  // authed viewer (especially the owner of the content). On mount, if
  // authenticated, refetch with the session token and override the poster
  // + unsafe-flag values. Player has its own session-aware stream fetch
  // (VideoPlayerClient.useEffect on streamUrl), so this only covers the
  // detail-page chrome.
  const [authPoster, setAuthPoster] = React.useState<string | null | undefined>(posterUrl);
  const [authUnsafe, setAuthUnsafe] = React.useState<boolean>(!!unsafe);
  // Audience as last reported by the api (SSR prop, then the authed refetch).
  const [authAudience, setAuthAudience] = React.useState<string | null | undefined>(audience);
  // Set ONLY on the recovery shell when the authed refetch answers 404: the
  // api says this video is not visible to this viewer (out of audience, or it
  // does not exist), so a placeholder player would be a dead end.
  const [notFound, setNotFound] = React.useState(false);
  React.useEffect(() => {
    setAuthPoster(posterUrl);
    setAuthUnsafe(!!unsafe);
  }, [posterUrl, unsafe]);
  React.useEffect(() => {
    setAuthAudience(audience);
  }, [audience]);
  React.useEffect(() => {
    if (status !== 'authenticated') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/api/video/v2/${id}`, session);
        if (cancelled) return;
        if (res.status === 404 && recoveryShell) {
          // Final answer for this viewer; the same session cannot recover it.
          setNotFound(true);
          return;
        }
        if (!res.ok) return;
        const j: any = await res.json().catch(() => null);
        if (cancelled || !j) return;
        // The api emits thumbnailKey (raw S3 key) preferred over thumbnailUrl
        // (which may be guest-blurred). Prefer the raw key when present.
        const nextPoster = j.thumbnailKey || j.thumbnailUrl || j.thumbUrl || null;
        if (nextPoster) setAuthPoster(nextPoster);
        if (typeof j.isUnsafeContent === 'boolean') setAuthUnsafe(j.isUnsafeContent);
        if (typeof j.audience === 'string') setAuthAudience(j.audience);
      } catch {
        // Non-fatal — SSR data stays in effect on refetch failure.
      }
    })();
    return () => { cancelled = true; };
  }, [status, session, id, recoveryShell]);

  async function tryHead(url: string, durationSec?: number) {
    try {
      // HEAD the resolved stream target to read size; CORS must allow HEAD + expose Content-Length
      const res = await fetch(url, { method: 'HEAD' });
      if (!res.ok) throw new Error(`HEAD ${res.status}`);
      const len = res.headers.get('content-length');
      const ct = res.headers.get('content-type') || '';
      const size = len ? Number(len) : undefined;
      let bitrateKbps: number | undefined = undefined;
      if (size && durationSec && durationSec > 0) {
        // bits / second -> kbps
        bitrateKbps = Math.round((size * 8) / durationSec / 1000);
      }
      setMeta((m) => ({ ...m, sizeBytes: size, bitrateKbps }));
      setHeadErr(null);
    } catch (e: any) {
      setHeadErr(e?.message || 'HEAD failed');
    }
  }

  const onStreamResolved = React.useCallback((url: string) => {
    setResolvedUrl(url);
    // Attempt size read if duration known
    const d = meta.durationSec || initialDurationSec || undefined;
    if (HEAD_PROBE) tryHead(url, d);
  }, [meta.durationSec, initialDurationSec, HEAD_PROBE]);

  const onMeta = React.useCallback((m: { durationSec?: number; width?: number; height?: number }) => {
    setMeta((prev) => ({ ...prev, ...m }));
    if (HEAD_PROBE && resolvedUrl && (m.durationSec || initialDurationSec)) tryHead(resolvedUrl, m.durationSec || initialDurationSec || undefined);
  }, [resolvedUrl, initialDurationSec, HEAD_PROBE]);

  function fmtDuration(sec?: number) {
    if (!sec || sec <= 0) return null;
    const s = Math.floor(sec);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    return h > 0 ? `${h}:${String(m).padStart(2,'0')}:${String(ss).padStart(2,'0')}` : `${m}:${String(ss).padStart(2,'0')}`;
  }

  function fmtSize(bytes?: number) {
    if (!bytes || bytes <= 0) return null;
    const mb = bytes / (1024 * 1024);
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    const gb = mb / 1024;
    return `${gb.toFixed(2)} GB`;
  }

  if (notFound) {
    // Recovery-shell only (see the notFound state above). Same wording as the
    // player's 404 message: says nothing about permission, which would imply
    // the video exists.
    return (
      <div className="rounded-3xl border border-zinc-200/60 bg-white p-10 text-center shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
        <h1 className="font-serif text-2xl font-semibold text-zinc-900 dark:text-zinc-50">This video isn&apos;t available.</h1>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">It may have been removed, or the link may be wrong.</p>
        <div className="mt-5 flex justify-center">
          <Link
            href="/videos"
            className="inline-flex items-center rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-accent/40"
          >
            Browse videos
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <VideoPlayerClient
        id={id}
        streamUrl={streamUrl}
        viewUrl={viewUrl}
        posterUrl={authPoster}
        unsafe={authUnsafe}
        title={title}
        titleHeading={titleHeading}
        description={description || undefined}
        viewCount={viewCount}
        gated={!!gated}
        likeCount={likeCount}
        isLiked={!!isLiked}
        createdAt={createdAt || undefined}
        uploader={uploader || undefined}
        initialDurationSec={initialDurationSec || undefined}
        showUnderPlayerMeta={true}
        onStreamResolved={onStreamResolved}
        onMeta={onMeta}
      />

      {/* About this video panel (no personal data) */}
      <section className="mt-2">
        <details className="group rounded-lg border bg-card text-card-foreground">
          <summary className="list-none cursor-pointer select-none px-4 py-2 md:px-5 md:py-3 flex items-center justify-between">
            <span className="font-medium">About this video</span>
            <svg className="h-4 w-4 transition-transform group-open:rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
          </summary>
          <div className="px-4 pb-3 md:px-5 md:pb-4 text-sm">
            {/* Description first */}
            {description ? (
              <p className="whitespace-pre-line text-muted-foreground">{scrubContactInfo(description)}</p>
            ) : (
              <p className="text-muted-foreground">No description provided.</p>
            )}
            {/* Grid of facts */}
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-muted-foreground">
              {createdAt && (
                <div><span className="font-medium text-foreground">Uploaded:</span> {new Date(createdAt).toLocaleString()}</div>
              )}
              {isPrivateAudience(authAudience) ? (
                <div>
                  <span className="font-medium text-foreground">Audience:</span>{' '}
                  <AudienceBadge audience={authAudience} variant="inline" />
                </div>
              ) : null}
              {meta.durationSec ? (
                <div><span className="font-medium text-foreground">Duration:</span> {fmtDuration(meta.durationSec)}</div>
              ) : null}
              {meta.width && meta.height ? (
                <div><span className="font-medium text-foreground">Resolution:</span> {meta.width}×{meta.height}</div>
              ) : null}
              {typeof meta.bitrateKbps === 'number' ? (
                <div><span className="font-medium text-foreground">Bitrate:</span> {meta.bitrateKbps.toLocaleString()} kbps</div>
              ) : (meta.sizeBytes ? (
                <div><span className="font-medium text-foreground">Size:</span> {fmtSize(meta.sizeBytes)}</div>
              ) : null)}
              {Array.isArray(tags) && tags.length > 0 ? (
                <div className="sm:col-span-2">
                  <span className="font-medium text-foreground">Tags:</span>{' '}
                  <span className="inline-flex flex-wrap gap-2 align-middle">
                    {tags.map((t, i) => (
                      <span key={`${t}-${i}`} className="inline-block rounded-full bg-muted px-2 py-0.5 text-xs">{scrubContactInfo(t)}</span>
                    ))}
                  </span>
                </div>
              ) : null}
              {headErr ? (
                <div className="sm:col-span-2 text-xs">Some technical details unavailable.</div>
              ) : null}
            </div>
          </div>
        </details>
      </section>

      {/* Comments removed per requirement */}
    </div>
  );
}
